import React from 'react';
import { createRoot } from 'react-dom/client';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { datadogRum } from '@datadog/browser-rum';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, ApolloClientProvider, VehicleImageThumbnailProvider } from 'cubedist-cube-pipeline-common-ui';
import AuthenticatorSelector from './components/AuthenticatorSelector/AuthenticatorSelector';
import { useAuthenticatorSelector } from './components/AuthenticatorSelector/useAuthenticatorSelector';
import ArrivalProvider from 'contexts/Arrival.context';
import FeaturesProvider from 'contexts/Features.context';
import NotificationProvider from 'contexts/Notification.context';
import RolesProvider from 'contexts/Roles.context';
import { getEnv } from './utils/environment';
import Router from './Router';
import theme from './theme';
import DatadogUserTracker from 'contexts/DatadogUserTracker.context';

const rootElement = document.getElementById('root');
const root = createRoot(rootElement);

const env = getEnv();

datadogRum.init({
  applicationId: '46f18288-cfb2-4c44-8241-0f1f4c1a23e4',
  clientToken: 'pub4b247c5549d01ca959a30da317eb10c0',
  site: 'datadoghq.com',
  service: 'cube-arrival-ui',
  env,
  sessionSampleRate: 100,
  sessionReplaySampleRate: 100,
  trackResources: true,
  trackLongTasks: true,
  trackUserInteractions: true,
});

datadogRum.setGlobalContextProperty('account_id', '091975348311');
datadogRum.startSessionReplayRecording();

const queryClient = new QueryClient();

const App = () => {
  const { selectedAuthenticator, selectAuthenticator } = useAuthenticatorSelector();

  if (selectedAuthenticator === null) {
    return <AuthenticatorSelector onSelect={selectAuthenticator} />;
  }

  return (
    <AuthProvider path="cube-pathname" search="" useEntra={selectedAuthenticator === 'entra'}>
      <ApolloClientProvider defaultClient="arrival">
        <QueryClientProvider client={queryClient}>
          <FeaturesProvider>
            <RolesProvider>
              <DatadogUserTracker />
              <NotificationProvider>
                <ArrivalProvider>
                  <VehicleImageThumbnailProvider>
                    <Router />
                  </VehicleImageThumbnailProvider>
                </ArrivalProvider>
              </NotificationProvider>
            </RolesProvider>
          </FeaturesProvider>
        </QueryClientProvider>
      </ApolloClientProvider>
    </AuthProvider>
  );
};

root.render(
  <ThemeProvider theme={theme}>
    <CssBaseline />
    <App />
  </ThemeProvider>,
);
